type ProgressSummary = {
  documentId: string;
  revisionId: string;
  revisionNumber: number;
  latestRevisionId: string;
  latestRevisionNumber: number;
  percentRead: number | null;
  currentPercentRead: number | null;
  hasVisited: boolean;
  hasUpdates: boolean;
  lastViewedRevisionId: string | null;
  lastViewedAt: string | null;
  lastReadRevisionId: string | null;
  lastReadAt: string | null;
  estimatedPercentRead?: number | null;
  currentEstimatedPercentRead?: number | null;
  manuallyCoveredPercent?: number | null;
  currentManuallyCoveredPercent?: number | null;
  lastManualCompletion?: { revisionId: string; revisionNumber: number; markedAt: string; estimatedPercentAtMark: number | null } | null;
};

const pageRoute = /^\/spaces\/[^/]+\/pages\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i;
const revisionId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const percentage = (value: number | null) => value === null ? "—" : `${Math.floor(Math.max(0, Math.min(100, value)))}%`;

export function initializeReadingLinks(root: Document = document) {
  const cache = new Map<string, ProgressSummary>();
  const refreshedAt = new Map<string, number>();
  const unavailable = new Set<string>();
  let active: HTMLAnchorElement | null = null;
  let scanPending = false;
  let fetching = false;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const tooltip = root.createElement("div");
  tooltip.className = "reading-tooltip";
  tooltip.id = "reading-progress-tooltip";
  tooltip.role = "tooltip";
  tooltip.hidden = true;
  root.body.append(tooltip);

  function key(element: HTMLElement) {
    return `${element.dataset.readingPageId}:${element.dataset.readingTargetRevision || ""}`;
  }

  function summaryFor(element: HTMLElement): ProgressSummary | undefined {
    if (element.dataset.readingProgress) {
      try {
        const summary = JSON.parse(element.dataset.readingProgress) as ProgressSummary;
        cache.set(key(element), summary);
        if (!refreshedAt.has(key(element))) refreshedAt.set(key(element), Date.now());
        return summary;
      } catch { /* A later batch can replace an incomplete swapped attribute. */ }
    }
    return cache.get(key(element));
  }

  function badge(anchor: HTMLAnchorElement, summary: ProgressSummary) {
    let indicator = anchor.querySelector<HTMLElement>(":scope > [data-reading-badge]");
    if (!indicator) {
      indicator = root.createElement("span");
      indicator.className = "reading-badge";
      indicator.dataset.readingBadge = "";
      indicator.dataset.readingIgnore = "";
      const value = root.createElement("span");
      value.dataset.readingValue = "";
      const updates = root.createElement("span");
      updates.className = "reading-badge__updates";
      updates.dataset.readingUpdated = "";
      updates.setAttribute("aria-hidden", "true");
      updates.textContent = "●";
      indicator.append(value, updates);
      anchor.append(indicator);
    }
    const value = indicator.querySelector<HTMLElement>("[data-reading-value]")!;
    const label = percentage(summary.percentRead);
    if (value.textContent !== label) value.textContent = label;
    indicator.querySelector<HTMLElement>("[data-reading-updated]")!.hidden = !summary.hasUpdates;
    indicator.setAttribute("aria-label", summary.percentRead === null ? "No measurable text"
      : `${label} read${summary.manuallyCoveredPercent ? ` including manual marks; time-based estimate ${percentage(summary.estimatedPercentRead ?? null)}` : " estimated"}${summary.hasUpdates ? "; updates since last checked" : ""}`);
    indicator.dataset.readingState = summary.hasUpdates ? "updated" : summary.percentRead === 100 ? "complete" : "partial";
  }

  function updateOwn(element: HTMLElement, summary: ProgressSummary) {
    element.querySelector<HTMLProgressElement>("progress")!.value = summary.percentRead ?? 0;
    element.querySelector<HTMLElement>("[data-reading-page-value]")!.textContent = percentage(summary.percentRead);
    const text = summary.percentRead === null ? "No measurable text"
      : summary.manuallyCoveredPercent ? `Includes manual marks · time-based estimate ${percentage(summary.estimatedPercentRead ?? null)}`
      : summary.hasUpdates ? "Updates since last checked"
      : summary.revisionId !== summary.latestRevisionId ? `Historical revision · current text ${percentage(summary.currentPercentRead)} read`
      : "Visible text and time on page";
    element.querySelector<HTMLElement>("[data-reading-page-detail]")!.textContent = text;
    const label = element.querySelector<HTMLElement>("[data-reading-page-label]");
    if (label) label.textContent = summary.manuallyCoveredPercent ? "Read" : "Estimated read";
  }

  function apply(element: HTMLElement, summary: ProgressSummary, fresh = false) {
    element.dataset.readingProgress = JSON.stringify(summary);
    cache.set(key(element), summary);
    if (fresh || !refreshedAt.has(key(element))) refreshedAt.set(key(element), Date.now());
    if (element instanceof HTMLAnchorElement) badge(element, summary);
    else if (element.matches("[data-own-reading-status]")) updateOwn(element, summary);
  }

  function annotate(anchor: HTMLAnchorElement) {
    if (anchor.dataset.readingPageId) return;
    if (anchor.closest(".reading-admin-panel, .reading-user-report, .space-menu-editor")) return;
    try {
      const url = new URL(anchor.href, location.href);
      const match = pageRoute.exec(url.pathname);
      if (url.origin !== location.origin || !match) return;
      const revision = url.searchParams.get("revision");
      if (revision && !revisionId.test(revision)) return;
      anchor.dataset.readingPageId = match[1].toLowerCase();
      if (revision) anchor.dataset.readingTargetRevision = revision.toLowerCase();
    } catch { /* Non-URL links are outside reading progress. */ }
  }

  async function scan() {
    scanPending = false;
    root.querySelectorAll<HTMLAnchorElement>(".page-tree a, [data-qprins-static-document] a, .document-breadcrumbs a, .search-results a, a[data-reading-page-id]").forEach(annotate);
    const missing = new Map<string, HTMLElement[]>();
    root.querySelectorAll<HTMLElement>("[data-reading-page-id]").forEach(element => {
      const summary = summaryFor(element);
      if (summary) apply(element, summary);
      if ((!summary || (element === active && Date.now() - (refreshedAt.get(key(element)) ?? 0) > 30_000)) && !unavailable.has(key(element))) {
        const list = missing.get(key(element)) ?? [];
        list.push(element);
        missing.set(key(element), list);
      }
    });
    if (!missing.size || fetching) return;
    fetching = true;
    try {
      const entries = [...missing.entries()];
      for (let offset = 0; offset < entries.length; offset += 200) {
        const batch = entries.slice(offset, offset + 200);
        for (const [target] of batch) refreshedAt.set(target, Date.now());
        const targets = batch.map(([target]) => target.endsWith(":") ? target.slice(0, -1) : target).join(",");
        const response = await fetch(`/ui/reading/progress?targets=${encodeURIComponent(targets)}`, {credentials: "same-origin"});
        if (!response.ok) break;
        const summaries = await response.json() as ProgressSummary[];
        for (const [target] of batch) {
          const [id, revision] = target.split(":");
          const summary = summaries.find(item => item.documentId === id && item.revisionId === (revision || item.latestRevisionId));
          if (summary) root.querySelectorAll<HTMLElement>("[data-reading-page-id]").forEach(element => {
            if (key(element) === target) apply(element, summary, true);
          });
          else unavailable.add(target);
        }
      }
      if (active && !tooltip.hidden) show(active);
    } catch { /* Saved server-rendered indicators remain useful while offline. */ }
    finally { fetching = false; }
  }

  function scheduleScan() {
    if (scanPending) return;
    scanPending = true;
    window.setTimeout(() => { void scan(); }, 50);
  }

  function hide() {
    tooltip.hidden = true;
    if (active) {
      const descriptions = (active.getAttribute("aria-describedby") || "").split(/\s+/).filter(value => value && value !== tooltip.id);
      if (descriptions.length) active.setAttribute("aria-describedby", descriptions.join(" "));
      else active.removeAttribute("aria-describedby");
    }
    active = null;
  }

  function addText(tag: string, text: string, className?: string) {
    const element = root.createElement(tag);
    element.textContent = text;
    if (className) element.className = className;
    tooltip.append(element);
  }

  function show(anchor: HTMLAnchorElement) {
    if (hideTimer) clearTimeout(hideTimer);
    if (active && active !== anchor) hide();
    active = anchor;
    tooltip.replaceChildren();
    const summary = summaryFor(anchor);
    if (Date.now() - (refreshedAt.get(key(anchor)) ?? 0) > 30_000) scheduleScan();
    if (!summary) {
      addText("p", "Reading progress unavailable");
      scheduleScan();
    } else {
      addText("strong", summary.percentRead === null ? "No measurable text" : `${percentage(summary.percentRead)} read`, "reading-tooltip__value");
      const progress = root.createElement("progress");
      progress.max = 100;
      progress.value = summary.percentRead ?? 0;
      progress.setAttribute("aria-label", "Estimated reading progress");
      tooltip.append(progress);
      addText("p", `Revision ${summary.revisionNumber}${summary.revisionId !== summary.latestRevisionId ? " · historical" : " · current"}`);
      if (summary.revisionId !== summary.latestRevisionId)
        addText("p", `Current revision ${summary.latestRevisionNumber}: ${percentage(summary.currentPercentRead)} read`);
      addText("p", !summary.hasVisited ? "Not opened yet" : summary.hasUpdates ? "Edits were made since you last checked" : "No updates since you last checked",
        summary.hasUpdates ? "reading-tooltip__updates" : undefined);
      if (summary.lastReadRevisionId)
        addText("p", summary.lastReadRevisionId === summary.latestRevisionId ? "Reading recorded on the current revision" : "Reading recorded on an older revision");
      if (summary.lastViewedAt)
        addText("p", `Last viewed ${new Date(summary.lastViewedAt).toLocaleString()}`, "reading-tooltip__date");
      if (summary.lastManualCompletion) {
        const mark = summary.lastManualCompletion;
        addText("p", `Manually marked revision ${mark.revisionNumber} · ${new Date(mark.markedAt).toLocaleString()}`);
        addText("p", `Time-based estimate: ${percentage(summary.estimatedPercentRead ?? null)}`);
      }
      addText("small", summary.manuallyCoveredPercent ? "Includes manual marks; timed reading is stored separately." : "Estimated from visible text and time on page.");
    }
    const descriptions = new Set((anchor.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));
    descriptions.add(tooltip.id);
    anchor.setAttribute("aria-describedby", [...descriptions].join(" "));
    tooltip.hidden = false;
    const bounds = anchor.getBoundingClientRect();
    const panel = tooltip.getBoundingClientRect();
    tooltip.style.left = `${Math.max(8, Math.min(bounds.left, innerWidth - panel.width - 8))}px`;
    tooltip.style.top = `${Math.max(8, bounds.bottom + panel.height + 12 <= innerHeight ? bounds.bottom + 8 : bounds.top - panel.height - 8)}px`;
  }

  const hoveredLink = (target: EventTarget | null) => target instanceof Element ? target.closest<HTMLAnchorElement>("a[data-reading-page-id]") : null;
  root.addEventListener("pointerover", event => { const anchor = hoveredLink(event.target); if (anchor) show(anchor); });
  root.addEventListener("pointerout", event => {
    const anchor = hoveredLink(event.target);
    if (anchor && !anchor.contains(event.relatedTarget as Node | null)) hideTimer = setTimeout(hide, 150);
  });
  tooltip.addEventListener("pointerenter", () => { if (hideTimer) clearTimeout(hideTimer); });
  tooltip.addEventListener("pointerleave", hide);
  root.addEventListener("focusin", event => { const anchor = hoveredLink(event.target); if (anchor) show(anchor); });
  root.addEventListener("focusout", event => { if (hoveredLink(event.target)) hide(); });
  root.addEventListener("keydown", event => { if (event.key === "Escape") hide(); });
  function repositionOrHide() {
    if (active && root.activeElement === active) {
      const bounds = active.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.top < innerHeight) { show(active); return; }
    }
    hide();
  }
  window.addEventListener("scroll", repositionOrHide, {passive: true});
  window.addEventListener("resize", repositionOrHide);
  root.addEventListener("qprins:reading-progress", event => {
    const summary = (event as CustomEvent<ProgressSummary>).detail;
    if (!summary?.documentId) return;
    root.querySelectorAll<HTMLElement>("[data-reading-page-id]").forEach(element => {
      if (element.dataset.readingPageId !== summary.documentId) return;
      const revision = element.dataset.readingTargetRevision;
      if (revision === summary.revisionId) apply(element, summary, true);
      else if (!revision) apply(element, {...summary, revisionId: summary.latestRevisionId, revisionNumber: summary.latestRevisionNumber,
        percentRead: summary.currentPercentRead, estimatedPercentRead: summary.currentEstimatedPercentRead,
        manuallyCoveredPercent: summary.currentManuallyCoveredPercent}, true);
    });
    if (active && !tooltip.hidden) show(active);
  });
  new MutationObserver(mutations => {
    if (mutations.some(mutation => [...mutation.addedNodes].some(node => node instanceof Element && !node.closest(".reading-tooltip, [data-reading-badge]")))) scheduleScan();
  }).observe(root.body, {childList: true, subtree: true});
  window.addEventListener("online", () => { unavailable.clear(); scheduleScan(); });
  void scan();
}
